import { lazy, Suspense } from "react";
import { createBrowserRouter, Navigate } from "react-router-dom";
import { PasswordGate, ProtectedRoute, RequireFeature, RequireRole } from "@/shared/auth/ProtectedRoute";
import { AppShell } from "@/widgets/AppShell";
import { Spinner } from "@/shared/ui/Spinner";
import { ChunkErrorBoundary, RouteError } from "./RouteError";

const LoginPage     = lazy(() => import("@/pages/LoginPage").then((m) => ({ default: m.LoginPage })));
const ControlCenterPage = lazy(() => import("@/pages/ControlCenterPage").then((m) => ({ default: m.ControlCenterPage })));
const KpiPage = lazy(() =>
  import("@/pages/KpiPage").then((m) => ({ default: m.KpiPage })),
);
const SitesPage      = lazy(() => import("@/pages/SitesPage").then((m) => ({ default: m.SitesPage })));
const SiteDetailPage = lazy(() => import("@/pages/SiteDetailPage").then((m) => ({ default: m.SiteDetailPage })));
const SensorsPage   = lazy(() => import("@/pages/SensorsPage").then((m) => ({ default: m.SensorsPage })));
const AlertsPage    = lazy(() => import("@/pages/AlertsPage").then((m) => ({ default: m.AlertsPage })));
const AlertDetailPage = lazy(() => import("@/pages/AlertDetailPage").then((m) => ({ default: m.AlertDetailPage })));
const ManagerDetailPage = lazy(() => import("@/pages/ManagerDetailPage").then((m) => ({ default: m.ManagerDetailPage })));
const AnomalyPage   = lazy(() => import("@/pages/AnomalyPage").then((m) => ({ default: m.AnomalyPage })));
const SettingsPage    = lazy(() => import("@/pages/SettingsPage").then((m) => ({ default: m.SettingsPage })));
const ProfilePage     = lazy(() => import("@/pages/ProfilePage").then((m) => ({ default: m.ProfilePage })));
const DigitalTwinPage = lazy(() => import("@/pages/DigitalTwinPage").then((m) => ({ default: m.DigitalTwinPage })));
const RiskPage      = lazy(() => import("@/pages/RiskPage").then((m) => ({ default: m.RiskPage })));
const ScenariosPage = lazy(() => import("@/pages/ScenariosPage").then((m) => ({ default: m.ScenariosPage })));
const ReportsPage   = lazy(() => import("@/pages/ReportsPage").then((m) => ({ default: m.ReportsPage })));
const BlueprintsPage = lazy(() => import("@/pages/BlueprintsPage").then((m) => ({ default: m.BlueprintsPage })));
const DevicesPage    = lazy(() => import("@/pages/DevicesPage").then((m) => ({ default: m.DevicesPage })));
const EvacuationPage = lazy(() => import("@/pages/EvacuationPage").then((m) => ({ default: m.EvacuationPage })));
const WorkEntriesPage = lazy(() => import("@/pages/WorkEntriesPage").then((m) => ({ default: m.WorkEntriesPage })));
const GasDbPage     = lazy(() => import("@/pages/GasDBPage").then((m) => ({ default: m.GasDbPage })));
const FieldCompassPage = lazy(() => import("@/pages/FieldCompassPage").then((m) => ({ default: m.FieldCompassPage })));
const CapturesPage  = lazy(() => import("@/pages/CapturesPage").then((m) => ({ default: m.CapturesPage })));
const CamerasPage   = lazy(() => import("@/pages/CamerasPage").then((m) => ({ default: m.CamerasPage })));
const UsersPage       = lazy(() => import("@/pages/admin/UsersPage").then((m) => ({ default: m.UsersPage })));
const OrgsPage        = lazy(() => import("@/pages/admin/OrgsPage").then((m) => ({ default: m.OrgsPage })));
const ChangePasswordPage = lazy(() => import("@/pages/ChangePasswordPage").then((m) => ({ default: m.ChangePasswordPage })));
// 개발 전용 미리보기 둘 — 프로덕션 번들에는 라우트·청크 모두 빠진다.
// ⛔ `import()` 자체를 DEV 조건 안에 둔다. 모듈 최상위의 `lazy(() => import(...))` 는 라우트에서 안 쓰여도
//    rollup 이 부작용 있는 호출로 보고 남겨, 청크가 만들어지고 서비스 워커 프리캐시에까지 들어갔다
//    (2026-09-29 빌드 확인: DispatchPreview-*.js). 조건 안에 두면 프로덕션에서 식이 null 로 접힌다.
// 119 승인 표면 미리보기(가짜 대기 2건).
const DispatchPreview = import.meta.env.DEV
  ? lazy(() => import("@/app/DispatchPreview").then((m) => ({ default: m.DispatchPreview })))
  : null;
// 관제 개편 시각 검수(가짜 금산 도면·센서노드·경보·2초 위험도 프레임, 로그인 없음).
const ControlPreview = import.meta.env.DEV
  ? lazy(() => import("@/app/preview/ControlPreview").then((m) => ({ default: m.ControlPreview })))
  : null;
// 도면 그림만(매니져 도면 PNG 를 웹 모양으로 뽑기, 로그인 없음).
const PlanPreview = import.meta.env.DEV
  ? lazy(() => import("@/app/preview/PlanPreview").then((m) => ({ default: m.PlanPreview })))
  : null;

/**
 * Every lazy page goes through here, so this is also where a **stale bundle**
 * is caught: after a deploy the hashed chunk an open tab asks for is gone, the
 * dynamic import rejects, and without a boundary React Router's default
 * developer screen replaced the entire console. `ChunkErrorBoundary` keeps the
 * shell mounted and reloads the tab once onto the new build.
 */
function Suspended({ children }: { children: React.ReactNode }) {
  return (
    <ChunkErrorBoundary>
      <Suspense
        fallback={
          <div className="grid place-items-center h-[calc(var(--vh)_*_0.6)] text-[var(--fg-subtle)]">
            <Spinner size={20} />
          </div>
        }
      >
        {children}
      </Suspense>
    </ChunkErrorBoundary>
  );
}

export const router = createBrowserRouter([
  ...(import.meta.env.DEV && DispatchPreview
    ? [{ path: "/__preview/dispatch", element: <Suspended><DispatchPreview /></Suspended> }]
    : []),
  ...(import.meta.env.DEV && ControlPreview
    ? [{ path: "/__preview/control", element: <Suspended><ControlPreview /></Suspended> }]
    : []),
  ...(import.meta.env.DEV && PlanPreview
    ? [{ path: "/__preview/plan", element: <Suspended><PlanPreview /></Suspended> }]
    : []),
  {
    path: "/login",
    errorElement: <RouteError />,
    element: (
      <Suspended>
        <LoginPage />
      </Suspended>
    ),
  },
  {
    element: <ProtectedRoute />,
    // Catch-all: anything the per-page ChunkErrorBoundary doesn't own (loaders,
    // the shell itself) lands here instead of React Router's default screen.
    errorElement: <RouteError />,
    children: [
      // Forced password change lives OUTSIDE the app shell + password gate so a
      // must-change user can actually reach it (the gate redirects here).
      { path: "change-password", element: <Suspended><ChangePasswordPage /></Suspended> },
      {
        element: <PasswordGate />,
        children: [
          {
            element: <AppShell />,
            children: [
              // ── 전 역할(worker 포함) — 작업자 4화면 + 계정 ────────────
              // 라우트 게이트는 navItems 의 minRole 과 짝으로 움직인다
              // (18 화면개편 §3-1: 메뉴에서 숨겨도 URL 직접 입력이면 열리던
              // 구멍을 여기서 막는다). 서버가 진짜 규칙이고 이건 UX 방어선.
              { index: true,         element: <Suspended><ControlCenterPage /></Suspended> },
              { path: "sensors",     element: <Suspended><SensorsPage /></Suspended> },
              { path: "alerts",      element: <Suspended><AlertsPage /></Suspended> },
              { path: "alerts/:id",  element: <Suspended><AlertDetailPage /></Suspended> },
              { path: "evacuation",  element: <Suspended><EvacuationPage /></Suspended> },
              // 작업 입·퇴실 기록(2026-10-27) — 통합관제 「작업 입실」 칸의 도착지. 메뉴는 운영원 이상만 보이지만
              // 열린 입실의 [작업 완료 퇴실]은 작업자도 쓰므로 주소는 전 역할에 연다(서버가 현장 접근을 다시 본다).
              { path: "work-entries", element: <Suspended><WorkEntriesPage /></Suspended> },
              // 관제 화면(NOC)은 통합 관제로 통합됨 — 기존 딥링크·`?kiosk=1`은
              // 같은 화면으로 유지한다.
              { path: "noc",         element: <Suspended><ControlCenterPage /></Suspended> },
              // 내 계정 = 나에 관한 것(정보·알림·보안), 설정 = 시스템·조직에
              // 관한 것. 계정 메뉴의 두 항목이 서로 다른 곳에 도착한다.
              { path: "profile",     element: <Suspended><ProfilePage /></Suspended> },

              // ── 운영원(operator) 이상 ─────────────────────────────────
              {
                element: <RequireRole min="operator" />,
                children: [
                  { path: "sites",       element: <Suspended><SitesPage /></Suspended> },
                  { path: "sites/:id",   element: <Suspended><SiteDetailPage /></Suspended> },
                  { path: "risk",        element: <Suspended><RiskPage /></Suspended> },
                  // 이상탐지 = 조기 징후 + 센서 건전성(flatline·drift) — 운영
                  // 도구라 operator 유지 (18 v2.1, 사용자 결정 2026-08-27).
                  { path: "anomaly",     element: <Suspended><AnomalyPage /></Suspended> },
                  // CCTV = 설정 > 기능 사용(cctv) 켜야 열림 — 현장 미설치(2026-09-30).
                  // 꺼져 있으면 옛 북마크·직접 주소도 조용히 `/` 로 보낸다.
                  {
                    element: <RequireFeature flag="cctv" />,
                    children: [
                      { path: "cameras", element: <Suspended><CamerasPage /></Suspended> },
                    ],
                  },
                  { path: "settings",    element: <Suspended><SettingsPage /></Suspended> },
                ],
              },

              // ── 팀장(safety_manager) 이상 — 대응 판단·제어·분석 ───────
              {
                element: <RequireRole min="safety_manager" />,
                children: [
                  // ⛔ `key` 가 없으면 두 라우트가 같은 위치·같은 타입이라 react-router 가
                  //    컴포넌트를 **언마운트하지 않는다** — variant prop 만 갈리고 마운트 때
                  //    한 번 정해지는 레이어 기본값(`layers.dispersion`)은 첫 진입값에 얼어붙는다.
                  //    그래서 3D 뷰로 먼저 들어갔다 시뮬레이터로 가면 가스가 아예 안 나오고,
                  //    반대 순서면 3D 뷰에서 가스가 움직여 「토글이 반대로 작동한다」로 읽혔다
                  //    (2026-09-23 지적). key 를 주면 모드를 바꿀 때마다 초기화가 다시 돈다.
                  { path: "dispersion",  element: <Suspended><DigitalTwinPage key="sim" variant="simulator" /></Suspended> },
                  { path: "twin",        element: <Suspended><DigitalTwinPage key="twin" /></Suspended> },
                  // 장치 제어 = 책임자 행위. 백엔드 command 는 이미 M+.
                  { path: "devices",     element: <Suspended><DevicesPage /></Suspended> },
                  { path: "managers/:id", element: <Suspended><ManagerDetailPage /></Suspended> },
                  // 통계개요는 보고서 화면의 「현황」 탭으로 합쳤다(2026-09-23 지시).
                  // 옛 주소를 404 로 만들면 북마크·문서 링크가 죽으므로 넘겨만 준다.
                  { path: "overview",    element: <Navigate to="/reports" replace /> },
                  { path: "reports",     element: <Suspended><ReportsPage /></Suspended> },
                  // SOP 모의훈련 기록 = KPI 증빙, 팀장 접근 필요 (18 §3-1-4).
                  { path: "scenarios",   element: <Suspended><ScenariosPage /></Suspended> },
                  // 가스 DB·도면은 **보기**는 사이트관리자부터(기준값 참조·도면 확인은 현장 운영에 필요).
                  // 편집(업로드·그리기·저장)은 페이지 안에서 org_admin 으로 막고 백엔드도 org_admin 이다.
                  // (2026-09-15 사용자: 「관리 기능들 다 어디 갔어… 권한에 맞게 보이게 해줘」)
                  { path: "gas-db",      element: <Suspended><GasDbPage /></Suspended> },
                  { path: "blueprints",  element: <Suspended><BlueprintsPage /></Suspended> },
                  // 현장 방위 교정(폰 나침반) — 설치·교정을 하는 사이트관리자가 쓴다. 백엔드 north 도 M+.
                  { path: "field-compass", element: <Suspended><FieldCompassPage /></Suspended> },
                ],
              },

              // ── 가람(org_admin) 이상 — 과제 증빙·자산 관리 ────────────
              {
                element: <RequireRole min="org_admin" />,
                children: [
                  // 협약 KPI 달성 현황 — 최종평가 관문. 고객 일상 업무 아님.
                  { path: "kpi",         element: <Suspended><KpiPage /></Suspended> },
                  // 캡처 = 기술사 검토 대상 자산, 고객 편집 금지. (가스 DB·도면 보기는 위 팀장 그룹)
                  { path: "captures",    element: <Suspended><CapturesPage /></Suspended> },
                  // ⛔ `/admin/…` 은 nginx 가 Django 관리자로 넘긴다 — 새로고침·주소 직접 입력이면
                  //    웹 화면 대신 Django 로그인으로 갔다(2026-10-29 운영 302 확인). 그래서 `/manage/…`.
                  //    옛 주소는 앱 안에서 누른 링크만 여기까지 오므로 넘겨만 준다.
                  { path: "manage/users", element: <Suspended><UsersPage /></Suspended> },
                  { path: "admin/users", element: <Navigate to="/manage/users" replace /> },
                ],
              },
              // ── 조직 관리 — 플랫폼 관리자 전용 ───────────────────────
              {
                element: <RequireRole min="super_admin" />,
                children: [
                  { path: "manage/orgs", element: <Suspended><OrgsPage /></Suspended> },
                  { path: "admin/orgs", element: <Navigate to="/manage/orgs" replace /> },
                ],
              },

              { path: "*",           element: <Navigate to="/" replace /> },
            ],
          },
        ],
      },
    ],
  },
]);
